import "./DiscountCodes.scss";
import { useState, useEffect } from "react";
import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  InputAdornment,
  TextField,
  useMediaQuery,
} from "@mui/material";
import EditUser from "./../../assets/edit-user.svg";
import DeleteUser from "./../../assets/delete.svg";
import SearchIcon from "@mui/icons-material/Search";
import { DataGrid } from "@mui/x-data-grid";
import { CouponsColumns } from "../../SourceData";
import { DatePicker, message } from "antd";
import MessageModals from "../../components/MessageModals/MessageModals";
import AddGif from "./../../assets/add-gif.gif";
import BlueButton from "../../components/BlueButton/BlueButton";
import SwipeableModals from "./SwipeableModals/SwipeableModals";
import api from "../../utils/api";
import SwitchButton from "../../components/SwitchButton/SwitchButton";
import usePermissions from "../../hooks/usePermissions";

const { RangePicker } = DatePicker;

const DiscountCodes = () => {
  const isMobile = useMediaQuery("(max-width:767px)");
  const isTablet = useMediaQuery("(min-width: 768px) and (max-width: 1024px)");
  const { hasPermission } = usePermissions();

  // State management
  const [coupons, setCoupons] = useState([]);
  const [query, setQuery] = useState("");
  const [dateRange, setDateRange] = useState(null);
  const [codeType, setCodeType] = useState("");
  const [currentModalView, setCurrentModalView] = useState("");
  const [openDelete, setOpenDelete] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedCoupon, setSelectedCoupon] = useState(null);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalRows, setTotalRows] = useState(0);

  const getData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page + 1, // Convert 0-based to 1-based page numbering
        limit: pageSize, // Changed from per_page to limit based on your API response
      });

      if (query.length >= 2) {
        params.append("search", query);
      }
      if (codeType) {
        params.append("type", codeType);
      }
      if (dateRange) {
        if (dateRange[0]) {
          params.append("date_from", dateRange[0].format("YYYY-MM-DD"));
        }
        if (dateRange[1]) {
          params.append("date_to", dateRange[1].format("YYYY-MM-DD"));
        }
      }

      const res = await api.get(`/coupons?${params.toString()}`);

      // Updated to match your API response structure
      setCoupons(res.data.data || []);
      setTotalRows(res.data.total || 0);
    } catch (error) {
      console.error("Error fetching coupons:", error);
      // Handle error - maybe show a message to user
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (query.length >= 2 || query.length === 0) {
        // Reset to first page when search/filter changes
        setPage(0);
        getData();
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [query, codeType, dateRange]);

  // Separate useEffect for pagination changes
  useEffect(() => {
    getData();
  }, [page, pageSize]);

  const handleActive = async (id) => {
    setLoading(true);
    try {
      await api.put(`/coupons/${id}/activateToggle`);
      getData();
      message.success("تم تغير الحالة بنجاح");
    } catch (error) {
      console.error("Error toggling coupon status:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpen = (view, coupon = null) => {
    setSelectedCoupon(coupon);
    setCurrentModalView(view);
    setIsOpen(true);
  };

  const handleClose = () => {
    setIsOpen(false);
    setSelectedCoupon(null);
  };

  const handleOpenDelete = (coupon) => {
    setSelectedCoupon(coupon);
    setOpenDelete(true);
  };

  const handleCloseDelete = () => {
    setOpenDelete(false);
    setSelectedCoupon(null);
  };

  const handlePaginationModelChange = (model) => {
    setPage(model.page);
    setPageSize(model.pageSize);
  };

  const actionColumn = [
    hasPermission(50) && {
      field: "active",
      headerName: "الحالة",
      minWidth: 40,
      headerAlign: "center",
      align: "center",
      sortable: false,
      renderCell: (params) => {
        const isActive = params?.row?.active === true;
        return (
          <SwitchButton
            checked={isActive}
            onClick={() => handleActive(params.row.id)}
          />
        );
      },
    },
    {
      field: "action",
      headerName: "",
      width: 180,
      headerAlign: "center",
      align: "center",
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        return (
          <div className="cell-action">
            {hasPermission(52) && (
              <div
                className="cell-btn"
                onClick={() => handleOpen("editDiscountCode", params.row)}
              >
                <img src={EditUser} alt="" />
              </div>
            )}
            {hasPermission(53) && (
              <div
                className="cell-btn"
                onClick={() => handleOpenDelete(params.row)}
              >
                <img src={DeleteUser} alt="" />
              </div>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <div className="users">
      <div className="special-header">
        <p className="title">اكواد الخصم</p>

        <div className="actions-container">
          <div className="search">
            <TextField
              id="outlined-search"
              type="search"
              value={query}
              placeholder="بحث..."
              onChange={(e) => setQuery(e.target.value)}
              sx={{
                width: isMobile ? "100%" : isTablet ? "300px" : "360px",
                borderRadius: "8px",
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />

            <RangePicker
              format={"MMM D"}
              style={{
                width: isMobile ? "100%" : isTablet ? "200px" : "300px",
                padding: "0px 10px",
                direction: "ltr",
              }}
              placeholder={["من", "الي"]}
              className="range-picker"
              onChange={setDateRange}
              value={dateRange}
            />
          </div>
          {hasPermission(51) && (
            <BlueButton
              text="اضافة كود خصم"
              size={isMobile ? "full" : isTablet ? "fit" : "large"}
              variant="fill"
              onClick={() => handleOpen("createDiscountCode")}
              icon={<img src={AddGif} alt="" />}
            />
          )}
        </div>

        <FormControl sx={{ width: isMobile ? "full" : "140px" }}>
          <InputLabel
            htmlFor="code-type"
            shrink
            sx={{
              background: "white",
              transformOrigin: "right top",
              transform: "translate(14px, -9px) scale(0.75)",
              right: 24,
              left: "auto",
              paddingLeft: "4px",
              paddingRight: "4px",
            }}
          >
            نوع الكود
          </InputLabel>
          <Select
            labelId="code-type-label"
            id="code-type"
            value={codeType}
            onChange={(e) => setCodeType(e.target.value)}
            displayEmpty={true}
            sx={{ borderRadius: "8px" }}
          >
            <MenuItem value="">الجميع</MenuItem>
            <MenuItem value="percent">نسبة مئوية</MenuItem>
            <MenuItem value="fixed">مبلغ ثابت</MenuItem>
          </Select>
        </FormControl>
      </div>

      <div className="container">
        <DataGrid
          sx={{ backgroundColor: "white", width: "100%" }}
          autoHeight={true}
          columns={CouponsColumns.concat(actionColumn.filter(Boolean))}
          rows={coupons.map((row, index) => ({
            ...row,
            index: page * pageSize + index + 1,
          }))}
          disableVirtualization
          disableColumnFilter
          disableColumnMenu
          loading={loading}
          paginationMode="server"
          rowCount={totalRows}
          pageSizeOptions={[10, 25, 50]}
          paginationModel={{
            page,
            pageSize,
          }}
          onPaginationModelChange={handlePaginationModelChange}
        />
      </div>

      <MessageModals
        open={openDelete}
        currentView="delete"
        route={`/coupons/${selectedCoupon?.id}`}
        onClose={handleCloseDelete}
        onDataChange={getData}
        message={"تم حذف الكود بنجاح"}
      />

      <SwipeableModals
        open={isOpen}
        onClose={handleClose}
        onOpen={handleOpen}
        currentView={currentModalView}
        coupon={selectedCoupon}
        onDataChange={getData}
      />
    </div>
  );
};

export default DiscountCodes;
